import { ButtonGroup, Dropdown, DropdownButton, Spinner } from "react-bootstrap";
import { useEffect, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { useTranslations } from "use-intl";
import { Card } from "@/components/Card";
import { getCurrencyHashrateChart$ } from "@/store/sub_accounts";
import { useAppDispatch } from "@/store";
import { formatHash } from "@/utils/to-hash";
import { CurrencyHashrateChartDto } from "@/models/sub_accounts";
import style from "@/components/HashrateChart/index.module.scss";
import cardStyle from "./index.module.scss";

const TIMES = ["10MIN", "HOUR", "DAY"];

interface IProps {
  currency: string;
  // Only these sub-accounts of the currency; all of them when omitted.
  subAccountNames?: string[];
}

// Screen 2 ("<Currency> / Все <Currency>") aggregate hashrate chart. The
// backend (SubAccountCurrencyHashrateChartView) adds up the charts it has
// stored for every sub-account on the partners' own timestamps, so the times
// come ready - no client-side date reconstruction needed. The note below says
// when some sub-accounts have no fresh chart and so are not in the total.
export function CurrencyHashrateChart({ currency, subAccountNames }: IProps) {
  const namesKey = (subAccountNames || []).join(",");
  const dispatch = useAppDispatch();
  const i18n = useTranslations("cabinet.statistics");
  const [currentTime, setCurrentTime] = useState("10MIN");
  const [loading, setLoading] = useState(true);
  // Sub-accounts without a fresh stored chart are not in the total.
  const [coverage, setCoverage] = useState({ charted: 0, total: 0 });
  const [series, setSeries] = useState<{ name: string; data: [number, number][] }[]>([
    { name: "Hashrate: ", data: [] },
  ]);
  const promise = useRef<any>();

  useEffect(() => {
    if (!currency) {
      return;
    }
    if (promise.current) {
      promise.current.abort();
    }
    setLoading(true);
    const dispatchReq = dispatch(
      getCurrencyHashrateChart$({
        currency,
        interval: currentTime,
        subAccountNames: namesKey,
      }),
    );
    promise.current = dispatchReq;
    dispatchReq
      .unwrap()
      .then((res: CurrencyHashrateChartDto) => {
        setLoading(false);
        setCoverage({ charted: res.charted, total: res.total });
        const data: [number, number][] = res.times.map((ts, idx) => [
          ts * 1000,
          res.values[idx],
        ]);
        setSeries([{ name: "Hashrate: ", data }]);
      })
      .catch(() => {
        setLoading(false);
        setCoverage({ charted: 0, total: 0 });
        setSeries([{ name: "Hashrate: ", data: [] }]);
      });
    return () => {
      dispatchReq.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currency, currentTime, namesKey]);

  const options = {
    chart: {
      id: "currency-hashrate-chart",
      type: "area",
      height: 300,
      toolbar: { show: false },
      zoom: { autoScaleYaxis: true },
    },
    dataLabels: { enabled: false },
    markers: { size: 3, colors: ["#3a83f1"], strokeColor: "#3a83f1", strokeWidth: 2 },
    xaxis: {
      type: "datetime",
      labels: {
        datetimeUTC: false,
        datetimeFormatter: {
          year: "yyyy",
          month: "MMM 'yy",
          day: "dd MMM",
          hour: "HH:mm",
        },
      },
    },
    yaxis: {
      labels: { formatter: (val: any) => formatHash(val, currency) },
    },
    stroke: { colors: ["#3a83f1"], width: 2 },
    tooltip: { x: { format: "dd MMM yyyy HH:mm" } },
    fill: {
      type: "gradient",
      gradient: { shade: "light", opacityFrom: 0.4, opacityTo: 0.05 },
    },
  };

  return (
    <Card className={cardStyle.card}>
      <div className={style.header}>
        <h5>{i18n("graphic")}</h5>
        <DropdownButton
          as={ButtonGroup}
          size="sm"
          variant="outline-secondary"
          title={currentTime}
        >
          {TIMES.map((item, idx) => (
            <Dropdown.Item
              eventKey={idx}
              key={idx}
              active={currentTime === item}
              onClick={() => setCurrentTime(item)}
            >
              {item}
            </Dropdown.Item>
          ))}
        </DropdownButton>
      </div>
      {loading ? (
        <div className={style.placeholder}>
          <Spinner />
        </div>
      ) : (
        <>
          {series[0].data.length ? (
            <Chart options={options as any} series={series} type="area" height={300} />
          ) : (
            <div className={style.placeholder}>{i18n("chartEmpty")}</div>
          )}
          {coverage.charted < coverage.total && (
            <p className={style.note}>
              {i18n("chartPartial", {
                missing: coverage.total - coverage.charted,
                total: coverage.total,
              })}
            </p>
          )}
        </>
      )}
    </Card>
  );
}
